import { useEffect, useMemo, useRef, useState } from 'react';
import { Trophy, X } from 'lucide-react';
import confetti from 'canvas-confetti';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const STORAGE_KEY = 'season_end_seen_v1';

// Sport-specific accent so each banner feels on-brand for its competition.
const SPORT_ACCENT = {
  AFL: { bg: '#E00000', pill: 'bg-[#E00000]/10 text-[#E00000]' },
  NRL: { bg: '#007A33', pill: 'bg-[#007A33]/10 text-[#007A33]' },
  EPL: { bg: '#37003C', pill: 'bg-[#37003C]/10 text-[#37003C]' },
  WC:  { bg: '#4338CA', pill: 'bg-[#4338CA]/10 text-[#4338CA]' },
};

function readSeen() {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}');
  } catch { return {}; }
}

function markSeen(compId, awardedAt) {
  const map = readSeen();
  map[compId] = awardedAt || new Date().toISOString();
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(map)); } catch { /* ignore */ }
}

// Group champions by competition so co-champions render on one banner.
function groupByComp(champs) {
  const map = new Map();
  (champs || []).forEach((c) => {
    if (!c?.competition_id) return;
    const bucket = map.get(c.competition_id) || {
      competition_id: c.competition_id,
      title: c.title,
      sport: c.sport,
      awarded_at: c.awarded_at,
      winners: [],
    };
    bucket.winners.push({ name: c.name, points: c.points });
    map.set(c.competition_id, bucket);
  });
  return Array.from(map.values());
}

function fireBurst(anchor, sport) {
  const rect = anchor.getBoundingClientRect();
  const origin = {
    x: (rect.left + rect.width / 2) / window.innerWidth,
    y: (rect.top + rect.height / 2) / window.innerHeight,
  };
  const color = SPORT_ACCENT[sport]?.bg || '#FFD700';
  confetti({
    particleCount: 80,
    spread: 65,
    origin,
    colors: [color, '#FFD700', '#FFFFFF'],
    scalar: 0.9,
    ticks: 220,
  });
  setTimeout(() => {
    confetti({
      particleCount: 40,
      spread: 90,
      origin,
      colors: [color, '#FFD700'],
      scalar: 1.1,
      ticks: 180,
    });
  }, 320);
}

export default function SeasonEndAnnouncement() {
  const [banners, setBanners] = useState([]);
  const [dismissed, setDismissed] = useState({});
  const refs = useRef({});

  useEffect(() => {
    let cancelled = false;
    axios.get(`${API_URL}/api/champions`, { withCredentials: true })
      .then((res) => {
        if (cancelled) return;
        const seen = readSeen();
        const groups = groupByComp(res.data || []).filter((g) => {
          if (!g.awarded_at) return false;
          const prev = seen[g.competition_id];
          return !prev || new Date(g.awarded_at) > new Date(prev);
        });
        setBanners(groups);
      })
      .catch(() => { /* silent — no banners on failure */ });
    return () => { cancelled = true; };
  }, []);

  // Fire confetti once per banner after render (respect reduced motion).
  useEffect(() => {
    if (banners.length === 0) return;
    const prefersReduced = window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches;
    if (prefersReduced) return;
    banners.forEach((b, i) => {
      const el = refs.current[b.competition_id];
      if (!el) return;
      setTimeout(() => fireBurst(el, b.sport), 200 + i * 600);
    });
  }, [banners]);

  const visible = useMemo(
    () => banners.filter(b => !dismissed[b.competition_id]),
    [banners, dismissed],
  );

  if (visible.length === 0) return null;

  const dismiss = (b) => {
    markSeen(b.competition_id, b.awarded_at);
    setDismissed(prev => ({ ...prev, [b.competition_id]: true }));
  };

  return (
    <div className="mb-8 space-y-3" data-testid="season-end-announcement">
      {visible.map((b) => {
        const accent = SPORT_ACCENT[b.sport] || { bg: '#1A1A1A', pill: 'bg-gray-100 text-gray-700' };
        const namesTxt = b.winners.length === 1
          ? b.winners[0].name
          : b.winners.map(w => w.name).join(' & ');
        const isCo = b.winners.length > 1;
        return (
          <div
            key={b.competition_id}
            ref={(el) => { refs.current[b.competition_id] = el; }}
            className="relative overflow-hidden rounded-md border shadow-sm animate-in fade-in slide-in-from-top-2 duration-500"
            style={{ borderColor: accent.bg }}
            data-testid={`season-end-banner-${b.sport.toLowerCase()}`}
          >
            {/* Sport-coloured left strip */}
            <div className="absolute top-0 bottom-0 left-0 w-1.5" style={{ backgroundColor: accent.bg }} />

            <div className="pl-6 pr-4 py-4 sm:py-5 flex flex-col sm:flex-row items-start sm:items-center gap-3 bg-white">
              <div
                className="w-11 h-11 rounded-full flex items-center justify-center flex-shrink-0"
                style={{ background: `linear-gradient(135deg, ${accent.bg}, ${accent.bg}CC)` }}
              >
                <Trophy className="w-5 h-5 text-white" />
              </div>

              <div className="flex-1 min-w-0">
                <div className={`inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-sm ${accent.pill} mb-1.5`}>
                  Season complete
                </div>
                <div className="text-base sm:text-lg font-bold text-[#1A1A1A] leading-snug">
                  🏆 {b.title} is a wrap — congrats {namesTxt}
                  {isCo && <span className="ml-1 text-xs font-semibold text-gray-500 uppercase tracking-wider">(co-champions)</span>}
                  !
                </div>
                {b.winners.length > 0 && (
                  <div className="text-xs text-gray-500 mt-0.5">
                    {b.winners.map((w, i) => (
                      <span key={w.name}>
                        {i > 0 && ' · '}
                        {typeof w.points === 'number' ? `${w.points.toFixed(2)} pts` : ''}
                        {b.winners.length > 1 && typeof w.points === 'number' ? ` (${w.name})` : ''}
                      </span>
                    ))}
                  </div>
                )}
              </div>

              <button
                type="button"
                onClick={() => dismiss(b)}
                className="text-gray-400 hover:text-[#1A1A1A] hover:bg-gray-100 rounded-sm p-1.5 transition-colors flex-shrink-0"
                aria-label="Dismiss"
                data-testid={`season-end-dismiss-${b.sport.toLowerCase()}`}
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
}
